import * as actionTypes from './actionTypes';
import * as eventTypes from './eventTypes';
import * as api from './api';
import { sendHttpRequest, sendHttpCommandAndWaitForEvent } from 'actions/actionCreators/httpActions';
import { showError } from 'actions/actionCreators/snackbarActions';

export function loadProductSelectionData(documentId, siteId) {
  return async (dispatch, getState) => {
    try {
      let { productDataLoading, productDataLoaded } = getState().currentOffer.productSelectionData.flags;
      if (productDataLoading && productDataLoaded) return;
      dispatch({ type: actionTypes.LOAD_PRODUCT_SELECTION_DATA_STARTED });
      const productSelectionData = await dispatch(sendHttpRequest(api.getProductSelectionData(documentId, siteId)));
      dispatch({ type: actionTypes.LOAD_PRODUCT_SELECTION_DATA_SUCCESS, payload: { productSelectionData } });
    } catch (error) {
      dispatch({ type: actionTypes.LOAD_PRODUCT_SELECTION_DATA_FAILED });
      dispatch(showError(error.message, error.correlationId));
      throw error.message;
    }
  };
}

export const addNewFinancialProduct = (documentId, siteId, financialProductId, callbackFn = () => {}) => {
  return dispatch => {
    dispatch(
      sendHttpCommandAndWaitForEvent(
        api.addNewFinancialProduct(documentId, siteId, financialProductId),
        'addNewFinancialProductCommand',
        [eventTypes.SIMULATION_VALIDATED],
        false
      )
    )
      .then(event => {
        if (event.ok) {
          dispatch({
            type: actionTypes.FINANCIAL_PRODUCT_ADD_SUCCEEDED,
            payload: {
              documentId,
              siteId
            }
          });
          callbackFn();
        } else {
          callbackFn(true);
          dispatch({
            type: actionTypes.FINANCIAL_PRODUCT_ADD_FAILED
          });
        }
      })
      .catch(err => {
        callbackFn(true);
        dispatch({
          type: actionTypes.FINANCIAL_PRODUCT_ADD_FAILED
        });
        dispatch(showError(err.message));
      });
  };
};

export const getFinancialProductsToAdd = (documentId, siteId, callbackFn = () => {}) => {
  return dispatch => {
    dispatch(
      sendHttpCommandAndWaitForEvent(
        api.getFinancialProductsToAdd(documentId, siteId),
        'getFinancialProductsToAddCommand',
        [eventTypes.FINANCIAL_PRODUCTS_TO_ADD_RETRIEVED],
        false
      )
    )
      .then(event => {
        if (event.ok) {
          callbackFn(event.eventBody.financialProductsToAdd, false);
          dispatch({
            type: actionTypes.FINANCIAL_PRODUCTS_TO_ADD_RETRIEVAL_SUCCEEDED
          });
        } else {
          callbackFn([], true);
          dispatch({
            type: actionTypes.FINANCIAL_PRODUCTS_TO_ADD_RETRIEVAL_FAILED
          });
        }
      })
      .catch(err => {
        dispatch({
          type: actionTypes.FINANCIAL_PRODUCTS_TO_ADD_RETRIEVAL_FAILED
        });
        dispatch(showError(err.message));
      });
  };
};

export function computeMaxResidualValuePercent(documentId, siteId, financingPeriod) {
  return async dispatch => {
    try {
      dispatch({ type: actionTypes.COMPUTE_OFFER_PRODUCTS_MAX_RESIDUAL_VALUE_STARTED });
      const event = await dispatch(
        sendHttpCommandAndWaitForEvent(
          api.computeOfferMaxResidualValuePercent(documentId, siteId, { durationMonths: financingPeriod }),
          'computeOfferResidualValueCommand',
          [eventTypes.OFFER_MAX_RESIDUAL_VALUE_COMPUTED],
          false
        )
      );
      if (event.ok) {
        dispatch({
          type: actionTypes.COMPUTE_OFFER_PRODUCTS_MAX_RESIDUAL_VALUE_SUCCEEDED,
          payload: {
            documentId,
            siteId,
            maxResidualValuePercent: event.eventBody.maxResidualValuePercent,
            residualValuePercent: event.eventBody.residualValuePercent
          }
        });
      } else {
        dispatch({
          type: actionTypes.COMPUTE_OFFER_PRODUCTS_MAX_RESIDUAL_VALUE_FAILED,
          payload: { documentId, siteId, financingPeriod, event }
        });
      }
    } catch (error) {
      dispatch({
        type: actionTypes.COMPUTE_OFFER_PRODUCTS_MAX_RESIDUAL_VALUE_FAILED,
        payload: { documentId, siteId, financingPeriod, error }
      });
      dispatch(showError(error.message, error.correlationId));
    }
  };
}

export function chooseFavoriteProduct(documentId, siteId, simulationDocumentId, simulationSiteId, priority) {
  return async dispatch => {
    try {
      dispatch({ type: actionTypes.FAVORITE_PRODUCT_STARTED });
      const event = await dispatch(
        sendHttpCommandAndWaitForEvent(
          api.chooseFavoriteProduct(documentId, siteId, simulationDocumentId, simulationSiteId, priority),
          'chooseFavoriteProductCommand',
          [eventTypes.FAVORITE_PRODUCT_COMPLETED],
          false
        )
      );
      if (event.ok) {
        dispatch({
          type: actionTypes.FAVORITE_PRODUCT_SUCCEEDED,
          payload: { simulationDocumentId, simulationSiteId, priority }
        });
      } else {
        dispatch({ type: actionTypes.FAVORITE_PRODUCT_FAILED, payload: { documentId, siteId, event } });
      }
    } catch (error) {
      dispatch({ type: actionTypes.FAVORITE_PRODUCT_FAILED, payload: { documentId, siteId, error } });
      dispatch(showError('Error choosing a favorite product: ' + error.message));
    }
  };
}

export function deleteProduct(documentId, siteId, simulationDocumentId, simulationSiteId) {
  return async dispatch => {
    try {
      dispatch({ type: actionTypes.DELETE_PRODUCT_STARTED });
      const event = await dispatch(
        sendHttpCommandAndWaitForEvent(
          api.deleteProduct(documentId, siteId, simulationDocumentId, simulationSiteId),
          'deleteProductCommand',
          [eventTypes.DELETE_PRODUCT_COMPLETED],
          false
        )
      );
      if (event.ok) {
        dispatch({ type: actionTypes.DELETE_PRODUCT_SUCCEEDED, payload: { simulationDocumentId, simulationSiteId } });
      } else {
        dispatch({ type: actionTypes.DELETE_PRODUCT_FAILED, payload: { documentId, siteId, event } });
      }
    } catch (error) {
      dispatch({ type: actionTypes.DELETE_PRODUCT_FAILED, payload: { documentId, siteId, error } });
      dispatch(showError('Error deleting a product: ' + error.message));
    }
  };
}

export function recomputeSimulations(
  documentId,
  siteId,
  financingPeriod,
  advancePercent,
  residualValuePercent,
  maxResidualPercent,
  agreedMileage,
  contractTypes
) {
  return async dispatch => {
    try {
      dispatch({ type: actionTypes.SIMULATIONS_RECOMPUTE_STARTED });
      await dispatch(
        sendHttpCommandAndWaitForEvent(
          api.recomputeSimulations(
            documentId,
            siteId,
            financingPeriod,
            advancePercent,
            residualValuePercent,
            maxResidualPercent,
            agreedMileage,
            contractTypes.filter(x => x.isSelected === true)
          ),
          'GenerateSimulationsRecalculateOfferValues',
          [eventTypes.SIMULATIONS_RECOMPUTE_COMPLETED],
          false
        )
      );
    } catch (error) {
      dispatch({ type: actionTypes.SIMULATIONS_RECOMPUTE_FAILED, payload: { documentId, siteId, error } });
      dispatch(showError('Error recomputing simulations: ' + error.message));
    }
  };
}

export function selectSimulation(documentId, siteId, productId, simulationDocumentId, simulationSiteId) {
  return async dispatch => {
    try {
      dispatch({ type: actionTypes.SIMULATIONS_SELECTION_STARTED, payload: { productId } });
      const event = await dispatch(
        sendHttpCommandAndWaitForEvent(
          api.selectSimulation(documentId, siteId, simulationDocumentId, simulationSiteId),
          'selectSimulationsCommand',
          [eventTypes.SIMULATION_SELECTION_COMPLETED],
          false
        )
      );
      if (event.ok) {
        dispatch({ type: actionTypes.SIMULATIONS_SELECTION_SUCCEEDED, payload: { productId } });
      } else {
        dispatch({ type: actionTypes.SIMULATIONS_SELECTION_FAILED });
      }
    } catch (error) {
      dispatch({ type: actionTypes.SIMULATIONS_SELECTION_FAILED });
      dispatch(showError('Error selecting simulation: ' + error.message));
    }
  };
}

export function setFinancialProductsViewMode(value) {
  return dispatch => {
    dispatch({ type: actionTypes.SET_FINANCIAL_PRODUCTS_VIEW_MODE, value });
  };
}
